import type { RoiId } from '~/domain/changes/ids';

/**
 * Most Z slices the preview will read back to draw one selection.
 *
 * The bound is slices rather than structures because that is what the cost
 * actually tracks. Loading a structure reads its SDF back from the clinical
 * context one slice at a time, and each of those synchronous reads costs about
 * a millisecond however small the slice is: measured cold, 30 slices took
 * 25 ms and 200 slices took 485 ms, though the second holds 500 times the data.
 * Counting structures would therefore price a whole-body External the same as a
 * lens, and twenty of them would stall the clinical context for seconds.
 *
 * At this budget an ordinary selection of organs, around fifty slices each,
 * draws about twenty of them, while a handful of body-sized structures uses it
 * up sooner. Either way the preview states how many of the selection are drawn.
 */
export const MAX_PREVIEW_SLICES = 1000;

/** Shortest interval between preview refreshes, ms — <= 4 Hz per the spec. */
export const MIN_UPDATE_INTERVAL_MS = 250;

/**
 * The selection, and the cheap test for whether an ROI is previewable at all.
 *
 * Deliberately holds nothing expensive: this is the half of the decision that
 * is pure derived state, recomputed whenever the selection changes.
 */
export interface PreviewSelection {
    /** The structure set's ROIs, in the set's own order. */
    setRoiOrder: readonly RoiId[];
    /** The active ROI, or null when nothing is selected. */
    selectedRoiId: RoiId | null;
    /** Bulk-selected ROIs; empty, or two or more. */
    multiSelectedRoiIds: ReadonlySet<RoiId>;
    /**
     * Whether the preview could draw this ROI at all: false for an ROI that is
     * not loaded, and for a point ROI, which has no surface to march.
     *
     * MUST be cheap: it is asked about every ROI in the selection, so it may
     * only read properties that are already in memory.
     */
    isDrawable: (roiId: RoiId) => boolean;
}

/**
 * How many Z slices drawing an ROI would read back, or null when it has no
 * content to draw yet.
 *
 * EXPENSIVE: answering forces the ROI's signed-distance field to exist and to
 * be read back from the GPU, the very cost {@link MAX_PREVIEW_SLICES} bounds.
 * {@link previewShownRoiIds} therefore asks it only about ROIs the budget
 * still has room for.
 */
export type SliceCount = (roiId: RoiId) => number | null;

/**
 * The ROIs the 3D preview could draw for a selection, in the set's own order:
 * everything selected except point ROIs and ROIs that are not loaded.
 *
 * This is what {@link previewShownRoiIds} is reported against, and what an
 * edit has to land on for the preview to care about it. It includes an ROI
 * whose contours are still empty: that is a structure the user selected and is
 * not seeing, which is what the preview's count exists to say, and it is the one
 * the next stroke is about to give something to draw.
 *
 * The preview is a tool, not a per-ROI window: it shows whatever is selected
 * and follows the selection as it changes, so growing a single selection into
 * a bulk one grows the preview with it rather than closing it. Whether the
 * tool is switched on at all is its host's decision, taken before this is
 * asked.
 */
export function previewDrawableRoiIds(
    selection: PreviewSelection
): readonly RoiId[] {
    const { setRoiOrder, selectedRoiId, multiSelectedRoiIds, isDrawable } =
        selection;

    const isSelected = (roiId: RoiId): boolean =>
        multiSelectedRoiIds.size >= 2
            ? multiSelectedRoiIds.has(roiId)
            : roiId === selectedRoiId;

    return setRoiOrder.filter(
        (roiId) => isSelected(roiId) && isDrawable(roiId)
    );
}

/**
 * Which of the drawable ROIs the preview actually draws, within
 * {@link MAX_PREVIEW_SLICES}.
 *
 * The input is in the set's own order, so neither the render order nor which ROIs
 * survive the budget depends on the order the rows happened to be clicked in:
 * the selection is taken in that order until the next ROI would not fit, and
 * the first one is always drawn however large it is, so asking for a structure
 * never yields an empty preview.
 *
 * The budget is walked lazily, so `sliceCount` is asked only about ROIs still
 * in the running. Probing the whole selection up front would spend the readback
 * cost the budget exists to bound before the budget had a chance to apply.
 */
export function previewShownRoiIds(
    drawable: readonly RoiId[],
    sliceCount: SliceCount
): readonly RoiId[] {
    const shown: RoiId[] = [];
    let slicesUsed = 0;
    for (const roiId of drawable) {
        const slices = sliceCount(roiId);
        if (slices === null || slices <= 0) {
            // No content to draw yet. Skipped rather than ending the walk: in
            // a bulk selection the ROIs after it are still worth showing.
            continue;
        }
        if (shown.length > 0 && slicesUsed + slices > MAX_PREVIEW_SLICES) {
            break;
        }
        shown.push(roiId);
        slicesUsed += slices;
    }
    return shown;
}
